import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  TextField,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@verantos-dev/shared-components';

import type { ConceptGroupOption } from './conceptGroupOption';

interface ConceptGroupAddButtonProps {
  availableConceptGroups: ConceptGroupOption[];
  selectedNames: string[];
  onSelect: (name: string) => void;
  onPreview?: (name: string) => void;
  onClose?: () => void;
  disabled?: boolean;
  /** When true, opens the dropdown programmatically (e.g., when trying to remove the last CG). */
  forceOpen?: boolean;
}

export const ConceptGroupAddButton = ({
  availableConceptGroups,
  selectedNames,
  onSelect,
  onPreview,
  onClose: onCloseProp,
  disabled = false,
  forceOpen = false,
}: ConceptGroupAddButtonProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setSearch('');
    }
  }, [disabled]);

  useEffect(() => {
    if (forceOpen && !disabled) {
      setOpen(true);
      setSearch('');
    }
  }, [forceOpen, disabled]);

  const selectedNamesLower = useMemo(
    () => new Set(selectedNames.map((n) => n.toLowerCase())),
    [selectedNames]
  );

  const filteredOptions = useMemo(() => {
    const query = search.toLowerCase();
    return availableConceptGroups.filter(
      (cg) =>
        !selectedNamesLower.has(cg.conceptGroupName.toLowerCase()) &&
        cg.conceptGroupName.toLowerCase().includes(query)
    );
  }, [availableConceptGroups, selectedNamesLower, search]);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
    setSearch('');
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearch('');
    onCloseProp?.();
  }, [onCloseProp]);

  const handleOptionClick = useCallback(
    (cg: ConceptGroupOption) => {
      onSelect(cg.conceptGroupName);
    },
    [onSelect]
  );

  return (
    <>
      <Button
        ref={anchorRef}
        size="small"
        startIcon={<AddIcon />}
        onClick={handleToggle}
        disabled={disabled}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 'auto' }}
      >
        Add
      </Button>
      {open && (
        <ClickAwayListener onClickAway={handleClose}>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            sx={{ zIndex: 1300 }}
          >
            <Paper
              elevation={8}
              sx={{
                width: 300,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ p: 1 }}>
                <TextField
                  placeholder="Search concept groups..."
                  size="small"
                  fullWidth
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Box>
              <List dense sx={{ maxHeight: 220, overflow: 'auto', py: 0 }}>
                {filteredOptions.map((cg) => (
                  <ListItemButton
                    key={cg.conceptGroupId}
                    onClick={() => handleOptionClick(cg)}
                    sx={{ py: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Checkbox size="small" checked={false} sx={{ p: 0 }} />
                    </ListItemIcon>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Icon
                        id={`cg-option-${cg.conceptGroupId}`}
                        icon={cg.owner?.toLowerCase() === 'verantos' ? 'verantosLogo' : 'owner'}
                        height={24}
                        width={24}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={cg.conceptGroupName}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                    {onPreview && (
                      <IconButton
                        size="small"
                        aria-label={`Preview ${cg.conceptGroupName}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onPreview(cg.conceptGroupName);
                        }}
                        sx={{ ml: 2, p: 0.5 }}
                      >
                        <Icon
                          icon="fullscreen"
                          id={`preview-${cg.conceptGroupName}`}
                          width={24}
                          height={24}
                          sx={{ fill: (theme) => theme.palette.text.secondary }}
                        />
                      </IconButton>
                    )}
                  </ListItemButton>
                ))}
                {filteredOptions.length === 0 && (
                  <Box sx={{ px: 2, py: 1, color: 'text.secondary', typography: 'body2' }}>
                    No concept groups available
                  </Box>
                )}
              </List>
            </Paper>
          </Popper>
        </ClickAwayListener>
      )}
    </>
  );
};
